import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { FleetEventEntry, FleetEventTransitionType } from '../../api/config';

const DEFAULT_BUCKET_COUNT = 96; // 24h at 15-min buckets
const DEFAULT_BUCKET_SIZE_MINUTES = 15;

/**
 * Sum totals for the visible window — real flows only (arrived / departed),
 * excluding the churn signals. Useful for a "day totals" badge alongside the
 * tile header so the number reads as "this site moved N units today."
 */
export function computeEventTotals(
  events: FleetEventEntry[],
  nowMs: number,
  bucketSizeMinutes: number = DEFAULT_BUCKET_SIZE_MINUTES,
  bucketCount: number = DEFAULT_BUCKET_COUNT,
): { arrivals: number; departures: number } {
  const cells = buildBuckets(events, nowMs, bucketCount, bucketSizeMinutes * 60 * 1000);
  let arrivals = 0, departures = 0;
  for (const c of cells) {
    arrivals += c.arrived;
    departures += c.departed;
  }
  return { arrivals, departures };
}

export interface EventVolumeChartProps {
  events: FleetEventEntry[];
  bucketWidth?: number;
  gap?: number;
  /** Total chart height (split evenly across the centerline). Default 28. */
  height?: number;
  bucketCount?: number;
  /** Bucket width in minutes. Match the heartbeat sparkline alongside this
      chart so columns align pixel-for-pixel. Default 15. */
  bucketSizeMinutes?: number;
  nowMs?: number;
  arrivedColor?: string;
  departedColor?: string;
  churnColor?: string;
}

interface BucketCell {
  startMs: number;
  // Up bars (positive direction)
  arrived: number;
  reacquired: number;
  // Down bars (negative direction)
  departed: number;
  falseDeparture: number;
}

function buildBuckets(
  events: FleetEventEntry[],
  nowMs: number,
  count: number,
  bucketSizeMs: number,
): BucketCell[] {
  const currentBucketStart = Math.floor(nowMs / bucketSizeMs) * bucketSizeMs;
  const lastClosedBucketStart = currentBucketStart - bucketSizeMs;
  const firstBucketStart = lastClosedBucketStart - (count - 1) * bucketSizeMs;

  const byStart = new Map<number, BucketCell>();
  for (let i = 0; i < count; i++) {
    const startMs = firstBucketStart + i * bucketSizeMs;
    byStart.set(startMs, {
      startMs,
      arrived: 0,
      reacquired: 0,
      departed: 0,
      falseDeparture: 0,
    });
  }

  for (const [iso, transition] of events) {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) continue;
    const bucketStart = Math.floor(ms / bucketSizeMs) * bucketSizeMs;
    const cell = byStart.get(bucketStart);
    if (!cell) continue;
    bumpCell(cell, transition);
  }

  return Array.from(byStart.values());
}

function bumpCell(cell: BucketCell, transition: FleetEventTransitionType) {
  switch (transition) {
    case 'arrived':
      cell.arrived++;
      break;
    case 'reacquired':
      cell.reacquired++;
      break;
    case 'departed':
      cell.departed++;
      break;
    case 'false_departure':
      cell.falseDeparture++;
      break;
  }
}

export const EventVolumeChart: React.FC<EventVolumeChartProps> = ({
  events,
  bucketWidth = 4,
  gap = 1,
  height = 28,
  bucketCount = DEFAULT_BUCKET_COUNT,
  bucketSizeMinutes = DEFAULT_BUCKET_SIZE_MINUTES,
  nowMs,
  arrivedColor = '#2e7d32',     // green up — real positive flow
  // Confirmed departure (lags ~45 min after the beacon stopped being seen).
  // Blue, not red: a departure is normal outbound flow, not an alarm. Red
  // stays reserved for genuine concerns (silence, late tiles, blackout
  // patterns) so the eye can disambiguate "shipping happened" from "fire."
  departedColor = '#1565c0',
  churnColor = '#f9a825',       // yellow — radio churn (reacquired up / false_departure down)
}) => {
  const effectiveNow = nowMs ?? Date.now();
  const bucketSizeMs = bucketSizeMinutes * 60 * 1000;
  const cells = React.useMemo(
    () => buildBuckets(events, effectiveNow, bucketCount, bucketSizeMs),
    [events, effectiveNow, bucketCount, bucketSizeMs]
  );

  // Independent peaks for up and down halves so the chart auto-scales
  // visually whether a site mostly arrives, mostly departs, or both.
  const peaks = React.useMemo(() => {
    let upPeak = 0;
    let downPeak = 0;
    for (const c of cells) {
      const up = c.arrived + c.reacquired;
      const down = c.departed + c.falseDeparture;
      if (up > upPeak) upPeak = up;
      if (down > downPeak) downPeak = down;
    }
    // Symmetric scale: pick the larger half so up/down stay comparable visually.
    const peak = Math.max(upPeak, downPeak, 1);
    return peak;
  }, [cells]);

  const halfH = height / 2;
  const totalWidth = bucketCount * bucketWidth + (bucketCount - 1) * gap;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${totalWidth} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Event volume — arrivals (up) and departures (down) per bucket"
      style={{ display: 'block' }}
    >
      {/* Centerline rendered as one segment per bucket — leaves the inter-bucket
          gap visually empty so adjacent volume bars read as separated, matching
          the heartbeat sparkline's column gaps. ~75 % gray for a clear axis. */}
      {cells.map((cell, idx) => {
        const x = idx * (bucketWidth + gap);
        return (
          <line
            key={`cl-${cell.startMs}`}
            x1={x}
            y1={halfH}
            x2={x + bucketWidth}
            y2={halfH}
            stroke="#404040"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}

      {cells.map((cell, idx) => {
        const x = idx * (bucketWidth + gap);
        const upPx = (cell.arrived / peaks) * halfH;
        const churnUpPx = (cell.reacquired / peaks) * halfH;
        const downPx = (cell.departed / peaks) * halfH;
        const churnDownPx = (cell.falseDeparture / peaks) * halfH;

        const tooltip = (
          <VolumeBucketTooltip
            cell={cell}
            bucketSizeMs={bucketSizeMs}
            arrivedColor={arrivedColor}
            departedColor={departedColor}
            churnColor={churnColor}
          />
        );

        return (
          <Tooltip key={cell.startMs} title={tooltip} arrow disableInteractive>
            <g>
              {/* Invisible hit-area rect — full column height so the tooltip
                  triggers anywhere over the bucket, not just the rendered bars. */}
              <rect x={x} y={0} width={bucketWidth} height={height} fill="transparent" />
              {/* Up: arrived (solid) on top of reacquired (yellow) — stacked from centerline */}
              {cell.reacquired > 0 && (
                <rect
                  x={x}
                  y={halfH - churnUpPx}
                  width={bucketWidth}
                  height={churnUpPx}
                  fill={churnColor}
                />
              )}
              {cell.arrived > 0 && (
                <rect
                  x={x}
                  y={halfH - churnUpPx - upPx}
                  width={bucketWidth}
                  height={upPx}
                  fill={arrivedColor}
                />
              )}
              {/* Down: departed (solid blue) on top of false_departure (yellow) — stacked from centerline */}
              {cell.falseDeparture > 0 && (
                <rect
                  x={x}
                  y={halfH}
                  width={bucketWidth}
                  height={churnDownPx}
                  fill={churnColor}
                />
              )}
              {cell.departed > 0 && (
                <rect
                  x={x}
                  y={halfH + churnDownPx}
                  width={bucketWidth}
                  height={downPx}
                  fill={departedColor}
                />
              )}
            </g>
          </Tooltip>
        );
      })}
    </svg>
  );
};

function formatBucketRange(startMs: number, bucketSizeMs: number): string {
  const start = new Date(startMs);
  const end = new Date(startMs + bucketSizeMs);
  const fmt = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${fmt(start)} – ${fmt(end)}`;
}

interface SwatchRowProps {
  color: string;
  label: string;
  value: number | string;
}

const SwatchRow: React.FC<SwatchRowProps> = ({ color, label, value }) => (
  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minHeight: 16 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        bgcolor: color,
        borderRadius: '50%',
        flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.4)',
      }}
    />
    <Typography variant="caption" sx={{ flex: 1, mr: 1 }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Stack>
);

interface VolumeBucketTooltipProps {
  cell: BucketCell;
  bucketSizeMs: number;
  arrivedColor: string;
  departedColor: string;
  churnColor: string;
}

const VolumeBucketTooltip: React.FC<VolumeBucketTooltipProps> = ({
  cell,
  bucketSizeMs,
  arrivedColor,
  departedColor,
  churnColor,
}) => {
  const total = cell.arrived + cell.departed + cell.reacquired + cell.falseDeparture;
  return (
    <Box sx={{ minWidth: 170 }}>
      <SwatchRow color={arrivedColor} label="Arrivals" value={cell.arrived} />
      <SwatchRow color={churnColor} label="Reacquisitions" value={cell.reacquired} />
      <SwatchRow color={departedColor} label="Departures" value={cell.departed} />
      <SwatchRow color={churnColor} label="False departures" value={cell.falseDeparture} />
      {total === 0 && (
        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
          No events
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
        {formatBucketRange(cell.startMs, bucketSizeMs)}
      </Typography>
    </Box>
  );
};
